import React from "react";
import { createStyles, Grid, makeStyles, Theme } from "@material-ui/core";
import { Helmet } from "react-helmet";
import Header from "../common/ToolsHeader";
import Settings from "./Settings";
import { connect, useSelector } from "react-redux";
import {
  getContestGenLineups,
  getContestLineups,
  getContestPlayers,
  getContestStacks,
  stopLoading,
} from "./store/actions";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Tabs from "./Tabs";
import StepsTracker from "./StepsTracker";
import { Stack } from "@mui/material";
import MobileCover from "../common/MobileCover";
import Authentication from "../common/Authentication";
import { SlateResponse } from "../common/store/types";
import { userSubscribed, useUserId } from "../utils/authentication";
import LoginRegisterContainer from "../common/LoginRegisterContainer";
import { IRootState } from "../../store/reducer";
import Subscribe from "../common/Subscribe";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      minHeight: "100vh",
      backgroundColor: "#000A16",
      color: theme.palette.common.white,
      fontFamily: "Roboto",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
    contentWrapper: {
      flexGrow: 1,
    },
  })
);

interface ReduxProps {
  readonly loading: boolean;
  readonly selectedSlate: SlateResponse;
  readonly poolSize: number;
  readonly liveContestId: string;
}

interface DispatchProps {
  readonly StopLoading: () => void;
  readonly GetContestPlayers: (slateId: string, user: string, liveContestId?: string, totalEntries?: number) => void;
  readonly GetContestGenLineups: (slateId: string, user: string) => void;
  readonly GetContestLineups: (slateId: string, user: string, liveContestId?: string, totalEntries?: number) => void;
  readonly GetContestStacks: (slateId: string, user: string) => void;
}

type Props = ReduxProps & DispatchProps;

const ContestGenerator = (props: Props) => {
  const classes = useStyles();
  const {
    loading,
    selectedSlate,
    poolSize,
    StopLoading,
    GetContestPlayers,
    GetContestLineups,
    GetContestGenLineups,
  } = props;
  const baseUrl = process.env.REACT_APP_API_URL || "";
  const userId = useUserId();
  const aweUserId = 'awe' + userId;
  const { authenticated, selectedSite, selectedSport } = useSelector((state: IRootState) => state.common);
  const isUserSubscribed = userSubscribed(authenticated, selectedSite, selectedSport);
  const [loadingPct, setLoadingPct] = React.useState("0%");
  const [activeStep, setActiveStep] = React.useState(1);
  const interval = React.useRef<ReturnType<typeof setInterval>>();
  const [entryFileUploaded, setEntryFileUploaded] = React.useState(false);
  const [statusType, setStatusType] = React.useState('ContestGen');
  const [useCustomProjections, setUseCustomProjections] = React.useState(false);

  const queryClient = new QueryClient();

  React.useEffect(() => {
    const statusCall = async () => {
      const progress = await fetch(
        `${baseUrl}/api/contests/getSimulationStatus?SlateId=${selectedSlate?.slateId || 5524
        }&User=${aweUserId}&StatusType=${statusType}`
      ).then((res) => res.json());

      if (progress.progress === 1) {
        StopLoading();
        if (statusType?.toLowerCase() === 'contestgen') {
          GetContestPlayers(selectedSlate?.slateId?.toString(), aweUserId);
          GetContestGenLineups(selectedSlate?.slateId?.toString(), aweUserId);
        } else if (statusType?.toLowerCase() === 'lateswap') {
          GetContestLineups(selectedSlate?.slateId?.toString(), aweUserId);
        }
      }
    };
    if (loading) {
      let totalMilliseconds = 0;
      let loadingTime = loadingTimeDict[`${selectedSlate.sport || 'NBA'}-${poolSize}`];
      loadingTime = loadingTime * 1.15;
      if (selectedSlate.type?.toUpperCase() === "SHOWDOWN") {
        loadingTime = loadingTime / 2;
      }
      if (statusType?.toLowerCase() === 'lateswap') {
        loadingTime = loadingTime * 1.5;
      }

      interval.current = setInterval(() => {
        if (loading && loadingTime > totalMilliseconds) {
          setLoadingPct(
            ((totalMilliseconds * 0.98 * 100) / loadingTime).toFixed(0)
          );
        }
        if (totalMilliseconds > 3000 && totalMilliseconds % 5000 === 4900) {
          statusCall();
        }
        totalMilliseconds += 10;
      }, 10);
      return () => {
        clearInterval(interval.current);
        setLoadingPct("0");
      };
    }
  }, [loading, statusType]);

  const loadingTimeDict: { [key: string]: number } = {
    "MLB-250": 30000,
    "MLB-500": 45000,
    "MLB-1000": 70000,
    "MLB-1500": 60000,
    "MLB-2000": 70000,
    "MLB-5000": 70000,
    "MLB-10000": 70000,
    "PGA-250": 30000,
    "PGA-500": 45000,
    "PGA-1000": 70000,
    "PGA-1500": 60000,
    "PGA-2000": 70000,
    "PGA-5000": 70000,
    "PGA-10000": 70000,
    "NBA-250": 30000,
    "NBA-500": 45000,
    "NBA-1000": 70000,
    "NBA-1500": 60000,
    "NBA-2000": 70000,
    "NBA-5000": 70000,
    "NBA-10000": 70000,
    "NFL-250": 30000,
    "NFL-500": 30000,
    "NFL-1000": 30000,
    "NFL-1500": 30000,
    "NFL-2000": 30000,
    "NFL-5000": 30000,
    "NFL-10000": 30000,
    "CFB-250": 30000,
    "CFB-500": 30000,
    "CFB-1000": 30000,
    "CFB-1500": 30000,
    "CFB-2000": 30000,
    "CFB-5000": 30000,
    "CFB-10000": 30000,
  };

  return (
    <QueryClientProvider client={queryClient}>
      <Authentication app='LATESWAP'>
      <Grid>
        <Helmet>
          <title>Late Swap</title>
          <meta name="description" content="Stokastic Late Swap" />
        </Helmet>
        <MobileCover>
          <Grid className={classes.root}>
            <Grid item xs={12}>
              <Header />
            </Grid>
            {
              userId ? (
                <Grid
                  item
                  xs={12}
                  className={classes.contentWrapper}
                >
                  <Stack spacing={0.5}>
                    {
                      !entryFileUploaded && (
                        <Settings onEntryFileUpload={(fileUploaded: boolean) => setEntryFileUploaded(fileUploaded)} setUseCustomProjections={() => setUseCustomProjections(!useCustomProjections)} useCustomProjections={useCustomProjections} />
                      )
                    }
                    {
                      isUserSubscribed ? (
                        entryFileUploaded && (
                          <>
                            <StepsTracker
                              setFileUploaded={(uploaded: boolean) => setEntryFileUploaded(uploaded)}
                              activeStep={activeStep}
                              setActiveStep={(step: number) => setActiveStep(step)}
                              />
                            <Tabs
                              loadingPercent={loadingPct}
                              setStatusType={(type: string) => setStatusType(type)}
                              useCustomProjections={useCustomProjections}
                              activeStep={activeStep}
                              setActiveStep={(step: number) => setActiveStep(step)}
                              />
                          </>
                        )
                      ) : <Subscribe toolName='Late Swap' />
                    }
                  </Stack>
                </Grid>
              ) : <LoginRegisterContainer toolName='Late Swap' />
            }
          </Grid>
        </MobileCover>
      </Grid>
      </Authentication>
    </QueryClientProvider>

  );
};

const mapStateToProps = (state: any) => {
  const { loading, poolSize, liveContestId } = state.lateSwap;

  const {
    selectedSlate
  } = state.common;

  return {
    loading,
    selectedSlate,
    poolSize,
    liveContestId
  };
};

const mapDispatchToProps = {
  StopLoading: stopLoading,
  GetContestPlayers: getContestPlayers,
  GetContestLineups: getContestLineups,
  GetContestGenLineups: getContestGenLineups,
  GetContestStacks: getContestStacks,
};

export default connect(mapStateToProps, mapDispatchToProps)(ContestGenerator);
